import httpClient from './auth-http-client'

/**
 * Everything stored on `users.notification_settings`. Named for push because
 * that is most of it, but `reengagement_email` is an email preference that
 * shares the same blob and endpoint.
 */
export interface PushPreferences {
    new_email: boolean
    relevant_only: boolean
    agent_messages: boolean
    suppress_while_active: boolean
    draft_comments: boolean
    draft_approved: boolean
    reengagement_email: boolean
    feedback_replies: boolean
    feedback_replies_email: boolean
}

async function getVapidPublicKey(): Promise<{ public_key: string | null, configured: boolean } | null> {
    try {
        const response = await httpClient.get('/push/vapid-public-key')
        if (response.status === 200) {
            return response.data
        }
        return null
    }
    catch (error) {
        console.error('Error:', error)
        return null
    }
}

async function subscribePush(subscription: PushSubscriptionJSON, userAgent?: string) {
    try {
        const response = await httpClient.post('/push/subscribe', {
            endpoint: subscription.endpoint,
            keys: subscription.keys,
            user_agent: userAgent,
        })
        return response.status === 200
    }
    catch (error) {
        console.error('Error:', error)
        return false
    }
}

async function unsubscribePush(endpoint: string) {
    try {
        const response = await httpClient.post('/push/unsubscribe', { endpoint })
        return response.status === 200
    }
    catch (error) {
        console.error('Error:', error)
        return false
    }
}

/**
 * Registers a native (FCM/APNs) device token. Counterpart to `subscribePush`
 * for the native app — see backend `POST /push/register-device`.
 */
async function registerDeviceToken(token: string, platform: 'ios' | 'android'): Promise<boolean> {
    try {
        const response = await httpClient.post('/push/register-device', {
            device_token: token,
            platform,
        })
        return response.status === 200
    }
    catch (error) {
        console.error('Error:', error)
        return false
    }
}

async function unregisterDeviceToken(token: string): Promise<boolean> {
    try {
        const response = await httpClient.post('/push/unregister-device', { device_token: token })
        return response.status === 200
    }
    catch (error) {
        console.error('Error:', error)
        return false
    }
}

async function getPushPreferences(): Promise<PushPreferences | null> {
    try {
        const response = await httpClient.get('/push/preferences')
        if (response.status === 200) {
            return response.data.preferences as PushPreferences
        }
        return null
    }
    catch (error) {
        console.error('Error:', error)
        return null
    }
}

async function updatePushPreferences(delta: Partial<PushPreferences>): Promise<PushPreferences | null> {
    try {
        const response = await httpClient.patch('/push/preferences', delta)
        if (response.status === 200) {
            return response.data.preferences as PushPreferences
        }
        return null
    }
    catch (error) {
        console.error('Error:', error)
        return null
    }
}

export {
    getPushPreferences,
    getVapidPublicKey,
    registerDeviceToken,
    subscribePush,
    unregisterDeviceToken,
    unsubscribePush,
    updatePushPreferences,
}
